import {
    Component, DestroyRef, Input, OnChanges, SimpleChanges, forwardRef,
    inject,
    HostBinding
} from "@angular/core";
import {
    AfterContentInit, ContentChildren, QueryList
} from "@angular/core";
import {
    ControlValueAccessor, NG_VALUE_ACCESSOR
} from "@angular/forms";
import {
    MatList,
} from "@angular/material/list";
import { SubformComponent } from "../subform/subform.component";
import {
    BehaviorSubject, Observable, Subject, takeUntil
} from "rxjs";
import { ObjectUtil } from "@shopliftr/common-js/data";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormListItemDirective } from "../../directives/form-list-item/form-list-item.directive";
import { EnhancedControlDirective } from "../../directives/form-control/enhanced-control.directive";

/**
 * A form control component that manages a list of items with add/remove functionality.
 * Implements ControlValueAccessor to work seamlessly with Angular reactive forms.
 *
 * @example
 * ```html
 * <mgd-form-list [formControl]="listControl" [subform]="mySubform">
 *   <ng-container *mgdFormListItem="let item">
 *     {{ item.name }}
 *   </ng-container>
 * </mgd-form-list>
 * ```
 *
 * @example
 * ```typescript
 * listControl = new FormControl([]);
 * ```
 */

@Component({
    selector: "mgd-form-list",
    imports: [
        MatList
    ],
    host: {
        "[class.mgd-form-list]": "true"
    },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FormListComponent),
            multi: true
        }
    ],
    hostDirectives: [{
        directive: EnhancedControlDirective,
        inputs: ["focusHotkey"]
    }],
    templateUrl: "./form-list.component.html",
    styleUrl: "./form-list.component.scss"
})
export class FormListComponent implements ControlValueAccessor, OnChanges, AfterContentInit {

    /**
     * Optional subform component that provides items to add to the list.
     * When the subform emits a submitSubform event, the item is automatically added.
     */
    @Input() subform?: SubformComponent;

    /**
     * FormListItemDirectives that are projected into the FormListItem
     *
     * @type {QueryList<FormListItemDirective>}
     * @memberof FormListComponent
     */
    @ContentChildren(FormListItemDirective) listItems!: QueryList<FormListItemDirective>;

    @HostBinding("class.subform-open")
    get subformOpen(): boolean {

        return this.subform?.open;
    }

    /**
     * Indicates whether the form list is disabled.
     * Set by the forms API via setDisabledState when the control status changes.
     */
    public disabled: boolean;

    /**
     * Callback function called when the list value changes.
     * Part of the ControlValueAccessor interface.
     */
    onChange?: (value: Array<unknown>) => void;

    /**
     * Callback function called when the control is touched.
     * Part of the ControlValueAccessor interface.
     */
    onTouched?: () => void;

    /** Internal BehaviorSubject that holds the current list items */
    private readonly _items$ = new BehaviorSubject<Array<unknown>>([]);

    /** Subject used to manage subform subscription lifecycle */
    private readonly _subformChange$ = new Subject();

    /** Angular DestroyRef for automatic subscription cleanup */
    private readonly _destroyRef = inject(DestroyRef);

    /**
     * Boolean that represents that there is a RequiredText FormListItemDirective among its ContentChildren
     *
     * @memberof FormListComponent
     */
    private _hasRequiredText: boolean = false;

    /**
     * Handles input property changes, particularly subform changes.
     * Sets up subscription to subform's submitSubform event.
     *
     * @param changes - The changed input properties
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes.subform) {
            this._subformChange$.next(undefined);
            // Setup submit Subform pipe
            this.subform?.submitSubform.pipe(
                takeUntil(this._subformChange$),
                takeUntilDestroyed(this._destroyRef)
            ).subscribe(
                (value) => {
                    this.addItem(value as unknown);
                }
            );

            // // Setup Edit Subform Pipe
            this.subform?.editSubform.pipe(
                takeUntil(this._subformChange$),
                takeUntilDestroyed(this._destroyRef)
            ).subscribe(
                (value) => {
                    this.editItem(value.formGroup, value.index);
                }
            );
        }
    }

    /**
     * Lifecycle hook that called after Angular has fully initialized all content of a directive.
     * Updates the _hasRequiredText flag based on the presence of a RequiredText FormListItemDirective.
     *
     * @memberof FormListComponent
     */
    ngAfterContentInit(): void {
        this._updateHasRequiredText();
        // update when projected content changes
        this.listItems.changes.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => this._updateHasRequiredText());
    }

    /**
     * Observable stream of the current list items.
     *
     * @returns Observable that emits the current array of items
     */
    get items$(): Observable<Array<unknown>> {
        return this._items$.asObservable();
    }

    /**
     * Gets the current list items as a snapshot.
     *
     * @returns Current array of items
     */
    get items(): Array<unknown> {
        return this._items$.value;
    }

    /**
     * True if there is a RequiredText ContentChild, False Otherwise
     * @return boolean represents if child content contains RequiredText type
     */
    get requiredError(): boolean {
        return this.items?.length === 0 && this._hasRequiredText;
    }

    /**
     * Adds a new item to the list and notifies form control of the change.
     *
     * @param item - The item to add to the list
     */
    addItem(item: unknown): void {
        const newValue = [...this._items$.value, item];
        this._items$.next(newValue);
        this.onChange(this.items);
    }

    /**
     * Edits item in the array at the index given
     * @param item
     * @param index
     * @memberof FormListComponent
     */
    editItem(item: unknown, index: number): void {
        const currentItems = this._items$.value;
        currentItems[index] = item;
        this._items$.next(currentItems);
        this.onChange(this.items);
    }

    /**
     * Removes an item from the list using deep equality comparison.
     * Notifies form control of the change.
     *
     * @param item - The item to remove from the list
     */
    removeItem(item: unknown): void {
        const newValue = this._items$.value.filter((i) => !ObjectUtil.equals(i, item));
        this._items$.next(newValue);
        this.onChange(this.items);
    }

    /**
     * Removes all items from the list and notifies form control of the change.
     */
    clear(): void {
        this._items$.next([]);
        this.onChange(this.items);
    }

    /**
     * Writes a new value to the component from the form control.
     * Part of the ControlValueAccessor interface.
     *
     * @param value - The new array value from the form control
     */
    writeValue(value: Array<unknown>): void {
        this._items$.next(value ?? []);
    }

    /**
     * Registers a callback function to be called when the value changes.
     * Part of the ControlValueAccessor interface.
     *
     * @param fn - Callback function to register
     */
    registerOnChange(fn: (value: Array<unknown>) => void): void {
        this.onChange = fn;
    }

    /**
     * Registers a callback function to be called when the control is touched.
     * Part of the ControlValueAccessor interface.
     *
     * @param fn - Callback function to register
     */
    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    /**
     * ControlValueAccessor Function that is called by the forms API when the control status changes to or from 'DISABLED'.
     * Depending on the status, it enables or disables the appropriate DOM element.
     * @param isDisabled boolean value that indicates whether the control should be disabled or not
     */
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    /**
     * Updates internal variable _hasRequiredText if there is an item with type requiredText among the Content Children
     */
    private _updateHasRequiredText(): void {
        // reassign _hasRequiredText if there is a requireText type among the listItems
        this._hasRequiredText = this.listItems.some((item: FormListItemDirective) => (item as any).type === "requiredText");
    }
}
